// Local-first persistence for intake messages. The IDB write is the
// authoritative confirmation that a turn was captured — the Supabase
// sync that follows is best-effort and recovered by RetryQueue.

import { openDB } from 'idb';

const DB_NAME = 'rooted-intake';
const DB_VERSION = 1;
const STORE = 'messages';
const MAX_RETRIES = 5;

export default class IndexedDBStore {
  constructor() {
    this._db = null;
  }

  async init() {
    if (this._db) return;
    this._db = await openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE)) {
          const store = db.createObjectStore(STORE, { keyPath: 'id' });
          store.createIndex('session_id', 'session_id', { unique: false });
          store.createIndex('sync_status', 'sync_status', { unique: false });
        }
      },
    });
  }

  async putMessage(record) {
    this._assertReady();
    await this._db.put(STORE, record);
    return record;
  }

  async getMessage(id) {
    this._assertReady();
    return (await this._db.get(STORE, id)) || null;
  }

  async getMessagesBySession(sessionId) {
    this._assertReady();
    const all = await this._db.getAllFromIndex(
      STORE,
      'session_id',
      sessionId
    );
    return all.sort((a, b) => a.turn_number - b.turn_number);
  }

  async getPendingMessages() {
    this._assertReady();
    const tx = this._db.transaction(STORE, 'readonly');
    const all = await tx.store.getAll();
    await tx.done;
    return all.filter(
      (m) =>
        (m.sync_status === 'pending' || m.sync_status === 'failed') &&
        (m.retry_count || 0) < MAX_RETRIES
    );
  }

  async markSynced(id) {
    this._assertReady();
    const record = await this._db.get(STORE, id);
    if (!record) return null;
    record.sync_status = 'synced';
    record.failure_reason = null;
    await this._db.put(STORE, record);
    return record;
  }

  async markFailed(id, reason) {
    this._assertReady();
    const record = await this._db.get(STORE, id);
    if (!record) return null;
    record.sync_status = 'failed';
    record.retry_count = (record.retry_count || 0) + 1;
    record.last_retry_at = new Date().toISOString();
    record.failure_reason = reason ? String(reason).slice(0, 500) : null;
    await this._db.put(STORE, record);
    return record;
  }

  _assertReady() {
    if (!this._db) {
      throw new Error('IndexedDBStore not initialized — call init() first');
    }
  }
}

export const MAX_SYNC_RETRIES = MAX_RETRIES;
